// src/lib/services/core/subscriptionService.ts
// Service for managing user subscriptions and Stripe integration

import { supabase } from '../../supabaseClient';
import type {
  Subscription,
  SubscriptionTier,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreatePortalSessionResponse,
} from '../../../types';

/**
 * Get user's subscription tier from their profile
 * Uses denormalized subscription_tier field for fast lookups
 */
async function getUserTier(userId: string): Promise<SubscriptionTier> {
  const { data, error } = await supabase
    .from('profiles')
    .select('subscription_tier, role')
    .eq('id', userId)
    .single();

  if (error || !data) {
    console.error('Error fetching user tier:', error);
    return 'free';
  }

  // Admin roles always have premium access
  if (data.role === 'power_admin' || data.role === 'content_admin') {
    return 'premium';
  }

  return (data.subscription_tier as SubscriptionTier) || 'free';
}

/**
 * Get user's full subscription details
 */
async function getSubscription(userId: string): Promise<Subscription | null> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching subscription:', error);
    return null;
  }

  return data as Subscription | null;
}

/**
 * Check if user has Ember AI access
 * Returns true if user is premium, admin, or has special access
 */
async function isEmberAllowed(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('profiles')
    .select('subscription_tier, role')
    .eq('id', userId)
    .single();

  if (error || !data) {
    return false;
  }

  // Admin roles always have access
  if (data.role === 'power_admin' || data.role === 'content_admin') {
    return true;
  }

  // Premium tier has access
  return data.subscription_tier === 'premium';
}

/**
 * Create a Stripe Checkout session for subscription
 */
async function createCheckoutSession(
  lookupKey: CreateCheckoutSessionRequest['lookup_key']
): Promise<CreateCheckoutSessionResponse> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch('/api/stripe/create-checkout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ lookup_key: lookupKey }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error ?? 'Failed to create checkout session');
  }

  return response.json();
}

/**
 * Create a Stripe Billing Portal session for subscription management
 */
async function createPortalSession(): Promise<CreatePortalSessionResponse> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch('/api/stripe/create-portal', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error ?? 'Failed to create portal session');
  }

  return response.json();
}

/**
 * Redirect to Stripe Checkout for subscription
 */
async function redirectToCheckout(
  lookupKey: CreateCheckoutSessionRequest['lookup_key']
): Promise<void> {
  const { url } = await createCheckoutSession(lookupKey);
  window.location.href = url;
}

/**
 * Redirect to Stripe Billing Portal
 */
async function redirectToPortal(): Promise<void> {
  const { url } = await createPortalSession();
  window.location.href = url;
}

export const subscriptionService = {
  getUserTier,
  getSubscription,
  isEmberAllowed,
  createCheckoutSession,
  createPortalSession,
  redirectToCheckout,
  redirectToPortal,
};
